import { useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { useGetDiscountCoupon } from './useGetDiscountCoupon';
import { useShoppingCartStore } from '@/store/shoppingCart/shoppingCartStore';
import { validateCouponAgainstCartItems } from '@/helpers/couponValidation';
import { useToken } from '../store/auth/tokenStore';
import { useUserProfile } from './useUserData';
import { AccessProfile } from '@/helpers/accessProfileUtil';

/**
 * Hook para aplicar cupom automaticamente via URL query parameter
 * Uso: /checkout?coupon=CODIGO123
 */
export const useApplyCouponFromUrl = () => {
    const { mutate, isPending } = useGetDiscountCoupon();
    const setCouponAction = useShoppingCartStore(state => state.setCoupon);
    const cartValues = useShoppingCartStore(state => state.cartValues);
    const appliedCoupon = useShoppingCartStore(state => state.coupon);

    const STORAGE_KEY = 'pending_coupon_code';
    const attemptedCouponRef = useRef<string | null>(null);
    const attemptedCodesRef = useRef<Set<string>>(new Set());
    const notifiedCodesRef = useRef<Set<string>>(new Set());
    const pendingToastTimeoutsRef = useRef<Map<string, number>>(new Map());
    const notifiedErrorCodesRef = useRef<Set<string>>(new Set());

    const userProfile = useUserProfile();

    const tryApply = useCallback((couponCode: string) => {
        if (!couponCode || isPending || attemptedCodesRef.current.has(couponCode)) return;

        attemptedCodesRef.current.add(couponCode);

        // evitar reaplicar cupom já aplicado
        if (appliedCoupon && appliedCoupon.value === couponCode) return;

        // limpar qualquer toast pendente para esse cupom (vamos controlar explicitamente)
        const existingTimeout = pendingToastTimeoutsRef.current.get(couponCode);
        if (existingTimeout) {
            clearTimeout(existingTimeout);
            pendingToastTimeoutsRef.current.delete(couponCode);
        }

        mutate(couponCode, {
            onSuccess: (data) => {
                // Se não houver itens no carrinho, guarda o cupom e notifica o usuário
                if (!cartValues || cartValues.length === 0) {
                    localStorage.setItem(STORAGE_KEY, couponCode);
                    toast.info('Cupom salvo e será aplicado quando houver itens no carrinho');
                    return;
                }

                // Validar regras do cupom contra o carrinho
                const validation = validateCouponAgainstCartItems(data, cartValues);
                if (!validation.isValid) {
                    toast.error(validation.errorMessage || 'Cupom inválido para o carrinho');
                    return;
                }

                setCouponAction(data);
                localStorage.removeItem(STORAGE_KEY);
                // limpar qualquer toast pendente
                const to = pendingToastTimeoutsRef.current.get(couponCode);
                if (to) {
                    clearTimeout(to);
                    pendingToastTimeoutsRef.current.delete(couponCode);
                }

                // Toast de sucesso após aplicar o cupom
                const discountDisplay = data.percentage 
                    ? `${data.percentage}%` 
                    : `R$ ${Number(data.discountValue).toFixed(2)}`;
                toast.success(`Cupom aplicado! Desconto: ${discountDisplay}`);
            },
            onError: () => {
                // limpar qualquer toast pendente
                const to = pendingToastTimeoutsRef.current.get(couponCode);
                if (to) {
                    clearTimeout(to);
                    pendingToastTimeoutsRef.current.delete(couponCode);
                }
                toast.error('Cupom inválido ou expirado');
            }
        });
    }, [isPending, appliedCoupon, cartValues, mutate, setCouponAction]);

    useEffect(() => {
        // Aceita apenas cupom via hash: exemplos válidos
        // - /checkout#coupon=SALTESTE
        // - /any/path#foo=1&coupon=SALTESTE
        const applyFromHash = () => {
            try {
                const hash = window.location.hash.replace(/^#/, '');
                if (!hash) return;
                const params = new URLSearchParams(hash);
                const couponCode = params.get('coupon');
                if (couponCode) {
                    localStorage.setItem(STORAGE_KEY, couponCode);
                    const token = useToken.getState().token;
                    const user = userProfile.data;
                    const isOwner = user && (
                        user.accessProfile?.enumKey === AccessProfile.Owner ||
                        user.accessProfile?.enumKey === AccessProfile.TAC_OWNER ||
                        user.accessProfile?.enumKey === AccessProfile.Dependent ||
                        user.accessProfile?.enumKey === AccessProfile.TAC_DEPENDENT
                    );
                    if (token && !isOwner) {
                        tryApply(couponCode);
                    } else if (isOwner) {
                        // Owner não pode aplicar cupom via link
                        // cancelar qualquer toast pendente e remover pending
                        const t = pendingToastTimeoutsRef.current.get(couponCode);
                        if (t) {
                            clearTimeout(t);
                            pendingToastTimeoutsRef.current.delete(couponCode);
                        }
                        if (!notifiedErrorCodesRef.current.has(couponCode)) {
                            toast.error('Esse cupom não está disponível para seu perfil de acesso');
                            notifiedErrorCodesRef.current.add(couponCode);
                        }
                        localStorage.removeItem(STORAGE_KEY);
                    } else if (!notifiedCodesRef.current.has(couponCode)) {
                        // adiar o toast de "cupom salvo" brevemente para evitar flash com erro
                        const timeoutId = window.setTimeout(() => {
                            // somente mostrar se o cupom ainda estiver pendente
                            if (localStorage.getItem(STORAGE_KEY) === couponCode && !notifiedCodesRef.current.has(couponCode)) {
                                toast.info('Cupom salvo. Faça login para aplicar.');
                                notifiedCodesRef.current.add(couponCode);
                            }
                            pendingToastTimeoutsRef.current.delete(couponCode);
                        }, 1000);
                        pendingToastTimeoutsRef.current.set(couponCode, timeoutId);
                    }
                }
            } catch (e) {
                /* ignore */
            }
        };

        // aplica ao montar
        applyFromHash();

        // aplica se a hash mudar
        window.addEventListener('hashchange', applyFromHash);
        return () => window.removeEventListener('hashchange', applyFromHash);
    }, [tryApply, userProfile.data]);

    // Tenta aplicar cupom salvo quando o carrinho muda
    useEffect(() => {
        const pending = localStorage.getItem(STORAGE_KEY);
        if (pending && cartValues && cartValues.length > 0) {
            // Evita aplicar o mesmo cupom múltiplas vezes
            if (attemptedCouponRef.current === pending) return;
            attemptedCouponRef.current = pending;
            tryApply(pending);
        }
    }, [cartValues, tryApply]);

    // Tenta aplicar cupom salvo quando o usuário faz login
    useEffect(() => {
        const unsubscribe = useToken.subscribe((state) => {
            if (state.token) {
                const pending = localStorage.getItem(STORAGE_KEY);
                const user = userProfile.data;
                const isOwner = user && (
                    user.accessProfile?.enumKey === AccessProfile.Owner ||
                    user.accessProfile?.enumKey === AccessProfile.TAC_OWNER ||
                    user.accessProfile?.enumKey === AccessProfile.Dependent ||
                    user.accessProfile?.enumKey === AccessProfile.TAC_DEPENDENT
                );
                if (pending) {
                    if (isOwner) {
                        // Owner não pode aplicar cupom
                        // cancelar timeout pendente
                        const t = pendingToastTimeoutsRef.current.get(pending);
                        if (t) {
                            clearTimeout(t);
                            pendingToastTimeoutsRef.current.delete(pending);
                        }
                        if (!notifiedErrorCodesRef.current.has(pending)) {
                            toast.error('Esse cupom não está disponível para seu perfil de acesso');
                            notifiedErrorCodesRef.current.add(pending);
                        }
                        localStorage.removeItem(STORAGE_KEY);
                        return;
                    }
                    if (!attemptedCodesRef.current.has(pending)) {
                        tryApply(pending);
                    }
                }
            }
        });
        return unsubscribe;
    }, [tryApply, userProfile.data]);
};
